import { useEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useApi, useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  Label,
  LoadingButton,
  SearchSelect,
} from '@fortem-monorepo/react-ui'
import { MarketSummary } from '@fortem-monorepo/web-sdk'

import { useMarketSummaryContext } from '@/pages/instruments/market-summary/market-summary-context'
import { useAddInstrumentMutation } from '@/pages/instruments/market-summary/market-summary.mutations'
import { InstrumentIdFormField } from '@/pages/trading/new-trade/fields/InstrumentIdFormField'
import { useInstrumentTypesQuery, useInstrumentsByTypeQuery } from '@/react-query/queries/instruments'

import { addInstrumentSchema, instrumentUnitFields } from './instrument.schema'

export interface AddInstrumentDialogProps extends DialogProps {
  onClose: () => void
}

export const AddInstrumentDialog: React.FC<AddInstrumentDialogProps> = ({ onClose, ...props }) => {
  const { instrumentsQuery: marketSummaryInstrumentsQuery } = useMarketSummaryContext()

  const existingInstrumentIds = marketSummaryInstrumentsQuery.data?.map((i) => i.instrument_id) ?? []

  const form = useFormWithSchema({ mode: 'all' }, addInstrumentSchema)

  const api = useApi()

  const bucketsQuery = useQuery({
    queryKey: ['market-summary', 'buckets'],
    queryFn: () => api.marketSummary.getBuckets(),
    enabled: Boolean(props.open),
  })

  const subBucketsQuery = useQuery({
    queryKey: ['market-summary', 'sub-buckets'],
    queryFn: () => api.marketSummary.getSubBuckets(),
    enabled: Boolean(props.open),
  })

  const valueTypesQuery = useQuery({
    queryKey: ['market-summary', 'value-types'],
    queryFn: () => api.marketSummary.getValueTypes(),
    enabled: Boolean(props.open),
  })

  const addInstrumentMutation = useAddInstrumentMutation()

  const [instrumentType, selectInstrumentType] = useState<number | null>()

  useEffect(() => {
    selectInstrumentType(null)
    form.reset()
    form.trigger()
  }, [props.open])

  const onSubmit = form.handleSubmit(async (_data) => {
    await addInstrumentMutation.mutateAsync({
      ..._data,
    })
    onClose()
  })

  const insturmentTypesQuery = useInstrumentTypesQuery()
  // @ts-ignore
  const instrumentsQuery = useInstrumentsByTypeQuery(instrumentType ? [instrumentType] : null)

  return (
    <Dialog
      {...props}
      onOpenChange={(open) => {
        if (!open && !form.formState.isSubmitting) {
          onClose()
        }
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Instrument</DialogTitle>
          <DialogDescription>This will add an instrument to use within the Market Summary.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Form {...form}>
            <FormItem>
              <div className="flex items-center gap-2">
                <Label>Instrument Type</Label>
              </div>
              <SearchSelect
                value={instrumentType?.toString()}
                setValue={(val) => (val !== undefined ? selectInstrumentType(parseInt(val)) : undefined)}
                loading={insturmentTypesQuery.isFetching}
                disabled={insturmentTypesQuery.isFetching}
                options={(insturmentTypesQuery.data ?? []).map((i) => ({
                  label: i.label,
                  value: i.value.toString(),
                }))}
                placeholder={'Select an instrument type...'}
              />
            </FormItem>
            <InstrumentIdFormField
              disabled={!instrumentType || instrumentsQuery.isFetching}
              loading={instrumentsQuery.isFetching}
              instruments={instrumentsQuery.data}
              disabledInstruments={existingInstrumentIds}
            />
            <FormField
              control={form.control}
              name="section"
              render={({ field: { value } }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Section</FormLabel>
                      <FormError />
                    </div>
                    <SearchSelect
                      value={value}
                      setValue={(val) => {
                        form.setValue('section', val as MarketSummary.Section, { shouldValidate: true })
                      }}
                      options={MarketSummary.SECTIONS.map((i) => ({
                        label: i,
                        value: i,
                      }))}
                      placeholder={'Select a section...'}
                    />
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name="bucket"
              render={({ field: { value } }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Bucket</FormLabel>
                      <FormError />
                    </div>
                    <SearchSelect
                      showNone={'None'}
                      value={value ? value : undefined}
                      setValue={(val) =>
                        form.setValue('bucket', val ? (val as MarketSummary.InstrumentBucket) : undefined, {
                          shouldValidate: true,
                        })
                      }
                      loading={bucketsQuery.isLoading}
                      disabled={bucketsQuery.isLoading || bucketsQuery.isError}
                      options={(bucketsQuery.data ?? []).map((i) => ({
                        label: i,
                        value: i.toString(),
                      }))}
                      placeholder={'Select a bucket...'}
                    />
                    {bucketsQuery.isError && (
                      <p className="text-sm text-destructive" role="alert">
                        Unable to load buckets.
                      </p>
                    )}
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name="sub_bucket"
              render={({ field: { value } }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Sub Bucket</FormLabel>
                      <FormError />
                    </div>
                    <SearchSelect
                      value={value ? value.toString() : undefined}
                      showNone={'None'}
                      setValue={(val) =>
                        form.setValue('sub_bucket', val ? (val as MarketSummary.InstrumentSubBucket) : undefined, {
                          shouldValidate: true,
                        })
                      }
                      loading={subBucketsQuery.isLoading}
                      disabled={subBucketsQuery.isLoading || subBucketsQuery.isError}
                      options={(subBucketsQuery.data ?? []).map((i) => ({ label: i, value: i }))}
                      placeholder={'Select a sub bucket...'}
                    />
                    {subBucketsQuery.isError && (
                      <p className="text-sm text-destructive" role="alert">
                        Unable to load sub buckets.
                      </p>
                    )}
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name="value_type"
              render={({ field: { value } }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Value Type</FormLabel>
                      <FormError />
                    </div>
                    <SearchSelect
                      value={value}
                      setValue={(val) =>
                        form.setValue('value_type', val as MarketSummary.InstrumentValueType, { shouldValidate: true })
                      }
                      loading={valueTypesQuery.isLoading}
                      disabled={valueTypesQuery.isLoading || valueTypesQuery.isError}
                      options={(valueTypesQuery.data ?? []).map((i) => ({ label: i, value: i }))}
                      placeholder={'Select a value type...'}
                    />
                    {valueTypesQuery.isError && (
                      <p className="text-sm text-destructive" role="alert">
                        Unable to load value types.
                      </p>
                    )}
                  </FormItem>
                )
              }}
            />
            {instrumentUnitFields.map((config) => (
              <FormField
                key={config.name}
                control={form.control}
                name={config.name}
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{config.label}</FormLabel>
                      <FormError />
                    </div>
                    <SearchSelect
                      value={field.value ?? undefined}
                      setValue={(value) => field.onChange(value ?? null)}
                      showNone="Unspecified"
                      options={MarketSummary.INSTRUMENT_UNITS.map((value) => ({ label: value, value }))}
                      placeholder={`Select ${config.label.toLowerCase()}...`}
                      disabled={form.formState.isSubmitting}
                    />
                  </FormItem>
                )}
              />
            ))}
          </Form>
        </div>
        <DialogFooter>
          <div className="flex items-center gap-2">
            <Button size="sm" type="button" variant={'destructive'} onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              size="sm"
              variant={'default'}
              onClick={onSubmit}
              disabled={!form.formState.isValid || form.formState.isSubmitting}
            >
              Create
            </LoadingButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
